/**
 * Source d'aléa. Unique point d'entrée du hasard dans le cœur crypto :
 * `crypto.getRandomValues` (WebCrypto), disponible sur navigateur, Node,
 * React Native (polyfill), WebView Android/iOS.
 *
 * ⚠️ `Math.random` est INTERDIT dans ce paquet.
 */
import { randomBytes as nobleRandomBytes } from '@noble/hashes/utils.js';
import { InsecureRandomnessError, InvalidParameterError } from './errors.js';
import { assertInteger } from './bytes.js';

/** Indique si un CSPRNG est disponible sur la plateforme courante. */
export function hasSecureRandom(): boolean {
  const g = globalThis as { crypto?: { getRandomValues?: unknown } };
  return typeof g.crypto?.getRandomValues === 'function';
}

/** Renvoie `length` octets issus du CSPRNG de la plateforme. */
export function randomBytes(length: number): Uint8Array {
  assertInteger(length, 'length', 1, 1 << 20);
  if (!hasSecureRandom()) throw new InsecureRandomnessError();
  const out = nobleRandomBytes(length);
  // Garde-fou : on refuse de renvoyer un tampon de mauvaise taille.
  if (out.length !== length) throw new InsecureRandomnessError();
  return out;
}

/**
 * Entier uniforme dans `[0, maxExclusive)` par échantillonnage avec rejet.
 *
 * Le rejet est indispensable : `random % n` introduit un biais modulo qui
 * réduit l'entropie réelle des mots de passe générés.
 */
export function randomInt(maxExclusive: number): number {
  assertInteger(maxExclusive, 'maxExclusive', 1, 0x100000000);
  if (maxExclusive === 1) return 0;
  const range = 0x100000000; // 2^32
  // Plus grand multiple de maxExclusive strictement inférieur ou égal à 2^32.
  const limit = range - (range % maxExclusive);
  const buffer = new Uint8Array(4);
  const view = new DataView(buffer.buffer);
  for (let attempt = 0; attempt < 1000; attempt++) {
    buffer.set(randomBytes(4));
    const draw = view.getUint32(0, false);
    if (draw < limit) return draw % maxExclusive;
  }
  // Probabilité < 2^-1000 avec un CSPRNG sain : indique un RNG défaillant.
  throw new InsecureRandomnessError();
}

/**
 * Mélange de Fisher-Yates (Durstenfeld) alimenté par le CSPRNG.
 * Utilisé pour ne pas révéler la position des caractères imposés par les
 * minimums du générateur de mots de passe.
 */
export function shuffleInPlace<T>(items: T[]): T[] {
  if (!Array.isArray(items)) throw new InvalidParameterError('items: tableau attendu');
  for (let i = items.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
  return items;
}

/** Choisit un élément uniformément dans un tableau non vide. */
export function randomPick<T>(items: readonly T[]): T {
  if (!Array.isArray(items) || items.length === 0) {
    throw new InvalidParameterError('items: tableau non vide attendu');
  }
  return items[randomInt(items.length)]!;
}
